import type { Locale } from '@iac/contracts';
import { useState } from 'react';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';
import { TagInput } from './TagInput';

const sample: Dictionary<{
  label: string;
  removeLabel: (tag: string) => string;
  suggestions: string[];
  initial: string[];
}> = {
  pl: {
    label: 'Tagi',
    removeLabel: (tag) => `Usuń tag ${tag}`,
    suggestions: ['obiad', 'szybkie', 'wege', 'zupa', 'święta', 'deser'],
    initial: ['zupa', 'wege'],
  },
  en: {
    label: 'Tags',
    removeLabel: (tag) => `Remove tag ${tag}`,
    suggestions: ['dinner', 'quick', 'veggie', 'soup', 'holidays', 'dessert'],
    initial: ['soup', 'veggie'],
  },
};

/** The input owns neither its tags nor its typed text; the form does. */
function Demo({ locale, filled }: { locale: Locale; filled: boolean }) {
  const t = sample[locale];
  const [tags, setTags] = useState<string[]>(filled ? t.initial : []);
  const [draft, setDraft] = useState('');
  return (
    <TagInput
      id={filled ? 'story-tags-filled' : 'story-tags-empty'}
      label={t.label}
      value={tags}
      onChange={setTags}
      draft={draft}
      onDraftChange={setDraft}
      suggestions={t.suggestions}
      removeLabel={t.removeLabel}
    />
  );
}

export default {
  kind: 'component',
  title: { pl: 'Pole tagów', en: 'Tag input' },
  description: {
    pl: 'Wolne tagi: pole podpowiada tagi już używane, a dodane pokazuje pod spodem jako chipy z przyciskiem usuwania. Enter albo przecinek dodaje wpisany tekst, Backspace w pustym polu cofa ostatni tag. Tag, który już jest (bez względu na wielkość liter i ogonki), nie dodaje się drugi raz.',
    en: 'Free tags: the field suggests tags already in use and shows the added ones below it as chips with a remove button. Enter or a comma adds the typed text, Backspace in an empty field takes the last tag back. A tag already there (ignoring case and accents) is not added twice.',
  },
  variants: [
    {
      name: { pl: 'Puste', en: 'Empty' },
      render: (locale) => <Demo locale={locale} filled={false} />,
    },
    {
      name: { pl: 'Z tagami', en: 'With tags' },
      render: (locale) => <Demo locale={locale} filled />,
    },
  ],
} satisfies Story;
